import { useMemo, useState } from "react";
import type { CatalogItem } from "@apex/shared";
import { cn, formatPrice } from "@apex/shared";
import { Card, CardContent } from "./ui/Card";
import { ImageLightbox } from "./ImageLightbox";
import { WidgetPortal } from "./WidgetPortal";

/** Spec keys to exclude from card display */
const HIDDEN_SPEC_KEYS = new Set([
  "baseprice", "price", "msrp", "currency", "basevehicle", "vehiclemake", "vehicletype",
]);

/** Priority order for hero metric */
const HERO_METRIC_PRIORITY = [
  "workingHeight", "workingOutreach", "workingLoad", "baseWeight",
];

const SPEC_LABELS: Record<string, string> = {
  workingHeight: "Height",
  workingOutreach: "Outreach",
  workingLoad: "Max Load",
  baseWeight: "Weight",
  boomRotation: "Rotation",
  basket: "Basket",
  basketSize: "Basket Size",
  boom: "Boom",
  engineType: "Engine",
};

export interface CatalogItemCardProps {
  rootItem: CatalogItem;
  onSelect: (rootItem: CatalogItem) => void;
  /**
   * Whether the parent CatalogItemType opts into pricing display. When
   * `false` (legacy CPL behaviour) the card hides any monetary price and
   * shows a "Get a quote" affordance instead. When `true` and `priceNet`
   * is set, the formatted price renders. When `true` but `priceNet` is
   * null, "Price on request" renders. Defaults to true so existing call
   * sites keep their previous behaviour until they pass the type-derived
   * value.
   */
  hasPricing?: boolean;
  /** Card index for stagger animation */
  index?: number;
  className?: string;
}

export function CatalogItemCard({
  rootItem,
  onSelect,
  hasPricing = true,
  index = 0,
  className,
}: CatalogItemCardProps) {
  const imageUrl = rootItem.images?.[0];
  const images = rootItem.images || [];
  const priceNet = rootItem.priceNet;
  const hasPrice =
    hasPricing && priceNet != null && priceNet > 0;
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const accessibleLabel = `Configure ${rootItem.name || rootItem.code}`;

  // Extract hero metric (top-right badge) and spec tags
  const { heroMetric, specTags } = useMemo(() => {
    if (!rootItem.metadata) return { heroMetric: null, specTags: [] };

    const entries = Object.entries(rootItem.metadata).filter(
      ([key]) => !HIDDEN_SPEC_KEYS.has(key.toLowerCase())
    );

    // Find best hero metric
    let hero: { label: string; value: string } | null = null;
    for (const key of HERO_METRIC_PRIORITY) {
      const entry = entries.find(([k]) => k === key);
      if (entry) {
        hero = { label: SPEC_LABELS[key] || key, value: entry[1] };
        break;
      }
    }

    // Spec tags: first 2 specs (excluding hero if found)
    const tags = entries
      .filter(([key]) => key !== (hero ? HERO_METRIC_PRIORITY.find(k => rootItem.metadata?.[k] === hero?.value && (SPEC_LABELS[k] || k) === hero?.label) : null))
      .slice(0, 2)
      .map(([key, value]) => ({
        label: SPEC_LABELS[key] || key.replace(/([A-Z])/g, " $1").trim().replace(/^\w/, c => c.toUpperCase()),
        value,
      }));

    return { heroMetric: hero, specTags: tags };
  }, [rootItem.metadata]);

  return (
    // The card is a plain container: its one real control is the Configure
    // button at the foot, whose stretched overlay makes the whole card its
    // click target. The expand-image button sits above that overlay (z-10).
    // A role="button" card nested those controls inside each other (axe
    // `nested-interactive`).
    <Card
      className={cn(
        "apex-group apex-relative apex-overflow-hidden apex-transition-all apex-duration-200",
        "hover:apex-shadow-card-hover hover:apex-border-primary/40 hover:-apex-translate-y-0.5",
        "has-[:focus-visible]:apex-ring-2 has-[:focus-visible]:apex-ring-primary has-[:focus-visible]:apex-ring-offset-2",
        "apex-animate-fade-in-up",
        className
      )}
      style={{ animationDelay: `${index * 60}ms` }}
    >
      {/* Image */}
      <div className="apex-relative apex-aspect-[4/3] apex-overflow-hidden apex-flex apex-items-center apex-justify-center" style={{ background: "var(--apex-gradient-surface)" }}>
        {imageUrl ? (
          <>
            <img
              src={imageUrl}
              alt={rootItem.name || rootItem.code}
              className="apex-max-h-full apex-max-w-full apex-object-contain apex-p-3 apex-transition-transform apex-duration-300 group-hover:apex-scale-105"
              loading="lazy"
            />
            {/* Bottom gradient overlay */}
            <div className="apex-absolute apex-inset-x-0 apex-bottom-0 apex-h-12 apex-bg-gradient-to-t apex-from-black/[0.06] apex-to-transparent apex-pointer-events-none" />

            {/* Expand button */}
            <button
              type="button"
              onClick={() => setLightboxOpen(true)}
              className="apex-absolute apex-z-10 apex-bottom-2 apex-right-2 apex-inline-flex apex-h-8 apex-w-8 apex-items-center apex-justify-center apex-rounded-full apex-bg-background/80 apex-shadow hover:apex-bg-background apex-transition-all apex-duration-200 apex-backdrop-blur-sm apex-opacity-0 group-hover:apex-opacity-100 focus-visible:apex-opacity-100 focus-visible:apex-outline-none focus-visible:apex-ring-2 focus-visible:apex-ring-ring"
              aria-label="Expand image"
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M15 3h6v6" />
                <path d="M9 21H3v-6" />
                <path d="M21 3l-7 7" />
                <path d="M3 21l7-7" />
              </svg>
            </button>
          </>
        ) : (
          <div className="apex-flex apex-h-full apex-w-full apex-items-center apex-justify-center apex-text-muted-foreground">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="48"
              height="48"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <rect width="18" height="18" x="3" y="3" rx="2" ry="2" />
              <circle cx="9" cy="9" r="2" />
              <path d="m21 15-3.086-3.086a2 2 0 0 0-2.828 0L6 21" />
            </svg>
          </div>
        )}

        {/* Hero metric badge */}
        {heroMetric && (
          <div className="apex-absolute apex-top-2 apex-right-2 apex-rounded-md apex-px-2 apex-py-1 apex-text-[11px] apex-font-semibold apex-leading-tight apex-bg-background/90 apex-text-foreground apex-backdrop-blur-sm" style={{ boxShadow: "0 1px 4px rgba(0,0,0,0.1)" }}>
            <span className="apex-text-muted-foreground apex-font-medium">{heroMetric.label}:</span>{" "}
            {heroMetric.value}
          </div>
        )}
      </div>

      <CardContent className="apex-p-4">
        {/* Title: h2, directly under the widget's h1 page title. */}
        <h2 className="apex-font-semibold apex-text-foreground apex-truncate apex-font-display">
          {rootItem.name || rootItem.code}
        </h2>

        {/* Code (if different from name) */}
        {rootItem.name && rootItem.name !== rootItem.code && (
          <p className="apex-text-sm apex-text-muted-foreground apex-truncate">
            {rootItem.code}
          </p>
        )}

        {/* Spec tags */}
        {specTags.length > 0 && (
          <div className="apex-flex apex-gap-1.5 apex-mt-2 apex-flex-wrap">
            {specTags.map((tag) => (
              <span
                key={tag.label}
                className="apex-inline-flex apex-items-center apex-rounded apex-bg-muted apex-px-2 apex-py-0.5 apex-text-[11px] apex-text-muted-foreground"
              >
                <span className="apex-font-medium apex-text-foreground/70">{tag.label}:</span>
                <span className="apex-ml-1">{tag.value}</span>
              </span>
            ))}
          </div>
        )}

        {/* Price + Configure */}
        <div className="apex-mt-4 apex-flex apex-items-center apex-justify-between">
          <div>
            {hasPrice ? (
              <span className="apex-text-lg apex-font-bold apex-text-foreground">
                {formatPrice(priceNet as number, rootItem.currency ?? undefined)}
              </span>
            ) : !hasPricing ? (
              <span className="apex-text-sm apex-text-muted-foreground">
                Get a quote
              </span>
            ) : (
              <span className="apex-text-sm apex-italic apex-text-muted-foreground">
                Price on request
              </span>
            )}
          </div>
          <button
            type="button"
            aria-label={accessibleLabel}
            onClick={() => onSelect(rootItem)}
            className={cn(
              "apex-group/btn apex-inline-flex apex-items-center apex-gap-1.5 apex-rounded-md apex-bg-primary apex-px-3.5 apex-py-2 apex-text-xs apex-font-semibold apex-text-primary-foreground apex-transition-all apex-duration-200 hover:apex-bg-primary/90 hover:apex-shadow-md",
              "focus-visible:apex-outline-none",
              // Stretched: the whole card is this button's click target.
              "after:apex-absolute after:apex-inset-0 after:apex-content-[''] after:apex-cursor-pointer",
            )}
          >
            Configure
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="12"
              height="12"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.5"
              strokeLinecap="round"
              strokeLinejoin="round"
              className="apex-transition-transform apex-duration-200 group-hover:apex-translate-x-0.5"
            >
              <path d="m9 18 6-6-6-6" />
            </svg>
          </button>
        </div>
      </CardContent>

      {/* Lightbox — portaled outside Card to escape overflow/transform containing block */}
      {lightboxOpen && images.length > 0 && (
        <WidgetPortal>
          <ImageLightbox
            images={images}
            initialIndex={0}
            alt={rootItem.name || rootItem.code}
            onClose={() => setLightboxOpen(false)}
          />
        </WidgetPortal>
      )}
    </Card>
  );
}
